import { AmountUsd } from '@ui/components/Amount/AmountUsd';
import { DEFAULT_UNDEFINED_NUMBER_DISPLAY } from '@ui/constants/uiConfig';
import { usePriceLive } from '@ui/hooks/price/usePriceLive';
import { useTokenDisplayDecimals } from '@ui/hooks/useTokenDisplayDecimals';
import { Position } from '@ui/services/contract/hooks/TradingReaderFacet/useGetPositionV2/types';
import { Nullable } from '@ui/types/utility';
import BigNumber from 'bignumber.js';
import { isNil } from 'lodash-es';
import { Stack } from 'styled-system/jsx';

import { useLiqPrice } from '../../hooks/useLiqPrice';
import { LiquidationStatusBadge } from './LiquidationStatusBadge';
import { valueStyle } from './styles';
import { PositionMarketData } from './types';
import { getLiquidationStatus } from './utils/getLiquidationStatus';

export const LiquidationPrice = ({
  position,
  marketData,
  priceDisplayDecimals,
}: {
  position: Position;
  marketData?: PositionMarketData;
  priceDisplayDecimals?: number;
}) => {
  if (marketData && priceDisplayDecimals !== undefined) {
    return (
      <LiquidationPriceValue
        position={position}
        marketPrice={marketData.pairMarketPrice}
        positionLiqPrice={marketData.liquidationPrice}
        priceDisplayDecimals={priceDisplayDecimals}
      />
    );
  }

  return <LiquidationPriceWithHooks position={position} />;
};

const LiquidationPriceWithHooks = ({ position }: { position: Position }) => {
  const marketPrice = usePriceLive(position.pair.baseSymbol);

  const positionLiqPrice = useLiqPrice({ genericTradeParams: position });
  const priceDisplayDecimals = useTokenDisplayDecimals(
    position?.pair.baseSymbol,
    position?.pair.priceDisplayDecimals,
  );
  return (
    <LiquidationPriceValue
      position={position}
      marketPrice={marketPrice}
      positionLiqPrice={positionLiqPrice}
      priceDisplayDecimals={priceDisplayDecimals}
    />
  );
};

const LiquidationPriceValue = ({
  position,
  marketPrice,
  positionLiqPrice,
  priceDisplayDecimals,
}: {
  position: Position;
  marketPrice?: Nullable<BigNumber>;
  positionLiqPrice?: Nullable<BigNumber>;
  priceDisplayDecimals: number;
}) => {
  const status =
    positionLiqPrice &&
    marketPrice &&
    getLiquidationStatus(positionLiqPrice, marketPrice);

  const isPendingPosition = position.isPendingPosition;

  if (isPendingPosition) return DEFAULT_UNDEFINED_NUMBER_DISPLAY;

  return (
    <Stack
      flexDirection={{
        base: 'row',
        desktop: 'column',
      }}
      alignItems={{
        base: 'center',
        desktop: 'flex-start',
      }}
      gap={0.5}
    >
      <AmountUsd
        // liq price reads as risk — short family (V2 spec)
        css={{ ...valueStyle, color: 'v2.short.default' }}
        value={positionLiqPrice}
        opts={{
          displayDecimals: priceDisplayDecimals,
        }}
        data-cy="LiqPrice"
        data-cy-value={positionLiqPrice}
      />
      {status && (
        <LiquidationStatusBadge
          status={status}
          display={
            isNil(positionLiqPrice) || isNil(marketPrice)
              ? 'none'
              : 'inline-flex'
          }
          data-cy="LiquidationStatusBadge"
          data-cy-value={status}
        />
      )}
    </Stack>
  );
};
